import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '@/hooks/use-toast'
import { fulfillmentApi, FulfillmentListParams } from '../api/fulfillmentApi'

export function useFulfillmentOrders(params: FulfillmentListParams = {}) {
  return useQuery({
    queryKey: ['fulfillment', params],
    queryFn: () => fulfillmentApi.getOrders(params),
  })
}

export function useFulfillmentSummary() {
  return useQuery({
    queryKey: ['fulfillment', 'summary'],
    queryFn: () => fulfillmentApi.getSummary(),
  })
}

export function useFulfillmentOrder(id: number | null) {
  return useQuery({
    queryKey: ['fulfillment', 'order', id],
    queryFn: () => fulfillmentApi.getOrder(id!),
    enabled: id !== null,
  })
}

export function useRefreshFulfillment() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation({
    mutationFn: (id: number) => fulfillmentApi.refresh(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fulfillment'] })
      toast({
        title: 'Status refreshed',
        description: `Order ${data.order.providerOrderId || data.order.id} synced (${data.order.status}).`,
      })
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Failed to refresh order',
        variant: 'destructive',
      })
    },
  })
}

// Dry-run preview: builds the provider payload without submitting.
export function useFulfillmentDryRun() {
  const { toast } = useToast()
  return useMutation({
    mutationFn: (shopifyOrderId: string | number) => fulfillmentApi.submit(shopifyOrderId, true),
    onError: (error: any) => {
      toast({
        title: 'Preview failed',
        description: error.response?.data?.message || 'Could not build the provider payload',
        variant: 'destructive',
      })
    },
  })
}

// Real submission (respects the server-side PRINT_FULFILLMENT_SUBMIT_ENABLED flag).
export function useSubmitFulfillment() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (shopifyOrderId: string | number) => fulfillmentApi.submit(shopifyOrderId, false),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fulfillment'] })
      if (data.submitted) {
        toast({ title: 'Submitted', description: `Provider order ${data.providerOrderId} created (${data.outcome || 'ok'}).` })
      } else {
        toast({ title: 'Not submitted', description: data.reason || 'Submission is disabled', variant: 'destructive' })
      }
    },
    onError: (error: any) => {
      toast({
        title: 'Submit failed',
        description: error.response?.data?.message || 'Failed to submit order',
        variant: 'destructive',
      })
    },
  })
}

export function useCancelFulfillment() {
  const queryClient = useQueryClient()
  const { toast } = useToast()
  return useMutation({
    mutationFn: (id: number) => fulfillmentApi.cancel(id),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['fulfillment'] })
      toast({ title: 'Cancel requested', description: `Order ${data.order.providerOrderId || data.order.id}: ${data.outcome || data.order.status}.` })
    },
    onError: (error: any) => {
      toast({
        title: 'Cancel failed',
        description: error.response?.data?.message || 'Failed to cancel order',
        variant: 'destructive',
      })
    },
  })
}
